import { useState, useEffect, useRef } from 'react'
import { useNews } from '@/hooks/useNews'
import DataSection from '@/components/ui/DataSection'
import { RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react'
import { format } from 'date-fns'
import { clsx } from 'clsx'
import TickerLink from '@/components/ui/TickerLink'
import PageHeader from '@/components/ui/PageHeader'
import NewsTape from '@/components/news/NewsTape'
import type { NewsFeedItem, SecFilingItem } from '@/lib/types'

// ── Page tabs ────────────────────────────────────────────────────────────────

const PAGE_TABS = ['tape', 'news', 'sec', 'breaking'] as const
type PageTab = (typeof PAGE_TABS)[number]

const PAGE_TAB_LABELS: Record<PageTab, string> = {
  tape: 'Live Tape',
  news: 'All News',
  sec: 'SEC Filings',
  breaking: 'Breaking',
}

// ── News category filters ────────────────────────────────────────────────────

const CATEGORIES = ['all', 'market', 'company', 'macro', 'crypto', 'politics'] as const
type Category = (typeof CATEGORIES)[number]

const CATEGORY_LABELS: Record<Category, string> = {
  all: 'All',
  market: 'Markets',
  company: 'Companies',
  macro: 'Macro',
  crypto: 'Crypto',
  politics: 'Politics',
}

// ── SEC form type filters ────────────────────────────────────────────────────

const SEC_FORM_TYPES = ['all', '8-K', '10-K', '10-Q', '4', '13F', 'S-1'] as const
type SecFormType = (typeof SEC_FORM_TYPES)[number]

const SEC_FORM_LABELS: Record<SecFormType, string> = {
  all: 'All',
  '8-K': '8-K',
  '10-K': '10-K',
  '10-Q': '10-Q',
  '4': 'Form 4',
  '13F': '13F',
  'S-1': 'S-1',
}

function secFormBadgeColor(formType: string): string {
  switch (formType) {
    case '8-K':    return 'badge-red'
    case '10-K':   return 'badge-blue'
    case '10-Q':   return 'badge-blue'
    case '4':      return 'badge-amber'
    case '13F':    return 'badge-green'
    case 'S-1':    return 'badge-purple'
    case 'DEF 14A': return 'badge-neutral'
    default:       return 'badge-neutral'
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function sourceBadgeColor(source: string): string {
  const s = source.toLowerCase()
  if (s.includes('finnhub')) return 'badge-blue'
  if (s.includes('finviz')) return 'badge-green'
  if (s.includes('sec') || s.includes('edgar')) return 'badge-amber'
  if (s.includes('reddit') || s.startsWith('r/')) return 'badge-amber'
  return 'badge-neutral'
}

function sentimentDot(sentiment: string): string {
  if (sentiment === 'positive') return 'bg-emerald-400'
  if (sentiment === 'negative') return 'bg-rose-400'
  return 'bg-text-muted'
}

function formatTimestamp(ts: string): string {
  if (!ts) return ''
  try {
    const num = Number(ts)
    if (!isNaN(num) && num > 1_000_000_000) {
      return format(new Date(num * 1000), 'HH:mm')
    }
    const d = new Date(ts)
    if (!isNaN(d.getTime())) {
      return format(d, 'HH:mm')
    }
  } catch { /* ignore */ }
  return ts.length > 16 ? ts.slice(0, 16) : ts
}

function formatFilingDate(ts: string): string {
  if (!ts) return ''
  try {
    const d = new Date(ts)
    if (!isNaN(d.getTime())) return format(d, 'MMM d')
  } catch { /* ignore */ }
  return ts.slice(0, 10)
}

// ── Scrolling ticker banner ──────────────────────────────────────────────────

function NewsTicker({ items }: { items: NewsFeedItem[] }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el || paused) return

    let animFrame: number
    const speed = 0.5

    const scroll = () => {
      if (el.scrollLeft >= el.scrollWidth - el.clientWidth) {
        el.scrollLeft = 0
      } else {
        el.scrollLeft += speed
      }
      animFrame = requestAnimationFrame(scroll)
    }
    animFrame = requestAnimationFrame(scroll)
    return () => cancelAnimationFrame(animFrame)
  }, [items, paused])

  if (items.length === 0) return null

  const doubled = [...items, ...items]

  return (
    <div
      className="relative bg-surface-card border border-border rounded-lg overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-surface-card to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-surface-card to-transparent z-10 pointer-events-none" />
      <div
        ref={scrollRef}
        className="flex items-center gap-8 px-6 py-2.5 overflow-x-hidden whitespace-nowrap"
        style={{ scrollBehavior: 'auto' }}
      >
        {doubled.map((item, i) => {
          const cls = clsx(
            'inline-flex items-center gap-2 text-xs shrink-0 transition-colors',
            item.urgency === 'high'
              ? 'text-rose-400 font-semibold'
              : 'text-text-secondary hover:text-text-primary',
          )
          const body = (
            <>
              <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', sentimentDot(item.sentiment))} />
              <span className="text-text-muted">{item.source}</span>
              <span className="truncate max-w-[360px]">{item.title}</span>
            </>
          )
          // No empty-href anchors — items without a URL render as plain text
          return item.url ? (
            <a key={i} href={item.url} target="_blank" rel="noopener noreferrer" className={cls}>
              {body}
            </a>
          ) : (
            <span key={i} className={cls}>{body}</span>
          )
        })}
      </div>
    </div>
  )
}

// ── All News tab ─────────────────────────────────────────────────────────────

function AllNewsTab({ items, loading }: { items: NewsFeedItem[]; loading: boolean }) {
  const [activeCategory, setActiveCategory] = useState<Category>('all')

  const filtered = activeCategory === 'all'
    ? items
    : items.filter(item => item.category === activeCategory)

  return (
    <div className="space-y-4">
      {/* Category filter */}
      <div className="glass rounded-xl p-1 flex items-center gap-1 overflow-x-auto">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeCategory === cat
                ? 'bg-blue/15 text-blue-light'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
            )}
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
        <span className="ml-auto text-xs text-text-muted pr-2">
          {filtered.length} headlines
        </span>
      </div>

      <div className="card overflow-hidden">
        <ul className="divide-y divide-border" aria-label="News feed">
          {filtered.map((item, idx) => (
            <li
              key={idx}
              className={clsx(
                'px-5 py-4 flex items-start gap-4 transition-colors hover:bg-surface-hover/50',
                item.urgency === 'high' && 'bg-rose-500/5 border-l-2 border-l-rose-500',
                item.urgency === 'medium' && 'bg-amber-500/5 border-l-2 border-l-amber-500',
              )}
            >
              <div className="w-14 shrink-0 text-right">
                <span className="text-xs font-mono text-text-muted">
                  {formatTimestamp(item.published_at)}
                </span>
              </div>

              <span
                className={clsx('w-2 h-2 rounded-full shrink-0 mt-1.5', sentimentDot(item.sentiment))}
                title={item.sentiment}
              />

              <span
                className={clsx(
                  'badge text-[10px] shrink-0 uppercase tracking-wider',
                  sourceBadgeColor(item.source),
                )}
              >
                {item.source.length > 12 ? item.source.slice(0, 12) : item.source}
              </span>

              <div className="flex-1 min-w-0">
                {/* The backend sends no summary/description — the old expander
                    just repeated the title as a fake description. The headline
                    now links straight to the article (when there is a URL). */}
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={clsx(
                      'text-sm leading-snug transition-colors inline-flex items-start gap-1.5 text-left',
                      item.urgency === 'high'
                        ? 'text-rose-300 font-semibold hover:text-rose-200'
                        : 'text-text-primary hover:text-blue-light',
                    )}
                  >
                    <span>{item.title}</span>
                    <ExternalLink size={11} className="shrink-0 mt-1" aria-hidden />
                  </a>
                ) : (
                  <span
                    className={clsx(
                      'text-sm leading-snug inline-flex items-start gap-1.5 text-left',
                      item.urgency === 'high'
                        ? 'text-rose-300 font-semibold'
                        : 'text-text-primary',
                    )}
                  >
                    {item.title}
                  </span>
                )}

                {(item.tickers_mentioned.length > 0 || item.category !== 'market') && (
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {item.tickers_mentioned.map(t => (
                      <TickerLink key={t} ticker={t} className="text-[11px]" />
                    ))}
                    <span className="text-[10px] text-text-muted uppercase tracking-wider">
                      {item.category}
                    </span>
                  </div>
                )}
              </div>
            </li>
          ))}

          {!loading && filtered.length === 0 && (
            <li className="px-5 py-12 text-center text-text-muted text-sm">
              No news available for this category.
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}

// ── SEC Filings tab ──────────────────────────────────────────────────────────

function SecFilingsTab({
  filings,
  loading,
  error,
  onFilterChange,
}: {
  filings: SecFilingItem[]
  loading: boolean
  error: string | null
  onFilterChange: (formType: string) => void
}) {
  const [activeForm, setActiveForm] = useState<SecFormType>('all')

  function handleFormChange(ft: SecFormType) {
    setActiveForm(ft)
    onFilterChange(ft)
  }

  return (
    <div className="space-y-4">
      {/* Form type filter */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {SEC_FORM_TYPES.map(ft => (
          <button
            key={ft}
            onClick={() => handleFormChange(ft)}
            className={clsx(
              'px-3 py-1.5 rounded-md text-xs font-medium transition-colors border',
              activeForm === ft
                ? 'bg-blue/15 text-blue-light border-blue/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover border-border',
            )}
          >
            {SEC_FORM_LABELS[ft]}
          </button>
        ))}
        <span className="ml-auto text-xs text-text-muted">
          {filings.length} filings
        </span>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-2 border-blue/30 border-t-blue rounded-full animate-spin" />
          </div>
        ) : (
          <div className="table-responsive overflow-x-auto">
          <table className="table-pro w-full text-sm" aria-label="SEC filings">
            <thead>
              <tr>
                <th className="px-4 py-2.5 w-20">Date</th>
                <th className="px-4 py-2.5">Company</th>
                <th className="px-4 py-2.5 w-16">Ticker</th>
                <th className="px-4 py-2.5 w-20">Form</th>
                <th className="px-4 py-2.5">Description</th>
              </tr>
            </thead>
            <tbody>
              {filings.map((filing, idx) => (
                <tr
                  key={idx}
                >
                  <td className="px-4 py-3 text-xs font-mono text-text-muted whitespace-nowrap">
                    {formatFilingDate(filing.filed_date)}
                  </td>
                  <td className="px-4 py-3 text-text-primary max-w-[200px] truncate" title={filing.company}>
                    {filing.company}
                  </td>
                  <td className="px-4 py-3">
                    {filing.ticker ? (
                      <TickerLink ticker={filing.ticker} className="text-[11px]" />
                    ) : (
                      <span className="text-text-muted text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx('badge text-[10px] uppercase tracking-wider', secFormBadgeColor(filing.form_type))}>
                      {filing.form_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-xs max-w-sm">
                    {filing.url ? (
                      <a
                        href={filing.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-start gap-1 hover:text-blue-light transition-colors"
                        title={filing.description}
                      >
                        <span className="line-clamp-2">{filing.description || 'View filing'}</span>
                        <ExternalLink size={11} className="shrink-0 mt-0.5" aria-hidden />
                      </a>
                    ) : (
                      <span className="line-clamp-2" title={filing.description}>
                        {filing.description || '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}

              {/* A failed fetch is an error, not "no filings" */}
              {!loading && error && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-rose-400 text-sm">
                    Failed to load SEC filings: {error}
                  </td>
                </tr>
              )}

              {!loading && !error && filings.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-text-muted text-sm">
                    No filings found for the selected filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Breaking News tab ────────────────────────────────────────────────────────

function BreakingTab({ breaking }: { breaking: NewsFeedItem[] }) {
  return (
    <div className="space-y-4">
      {breaking.length === 0 ? (
        <div className="card px-5 py-12 text-center text-text-muted text-sm">
          No breaking news at this time.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <ul className="divide-y divide-border" aria-label="Breaking news">
            {breaking.map((item, idx) => (
              <li
                key={idx}
                className={clsx(
                  'px-5 py-4 flex items-start gap-4 transition-colors hover:bg-surface-hover/50',
                  item.urgency === 'high'
                    ? 'bg-rose-500/5 border-l-2 border-l-rose-500'
                    : 'bg-amber-500/5 border-l-2 border-l-amber-500',
                )}
              >
                <div className="w-14 shrink-0 text-right">
                  <span className="text-xs font-mono text-text-muted">
                    {formatTimestamp(item.published_at)}
                  </span>
                </div>

                <span
                  className={clsx('w-2 h-2 rounded-full shrink-0 mt-1.5', sentimentDot(item.sentiment))}
                  title={item.sentiment}
                />

                <div className="flex-1 min-w-0">
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={clsx(
                        'text-sm leading-snug transition-colors hover:underline',
                        item.urgency === 'high'
                          ? 'text-rose-300 font-semibold hover:text-rose-200'
                          : 'text-amber-300 font-medium hover:text-amber-200',
                      )}
                    >
                      {item.title}
                    </a>
                  ) : (
                    <span
                      className={clsx(
                        'text-sm leading-snug',
                        item.urgency === 'high'
                          ? 'text-rose-300 font-semibold'
                          : 'text-amber-300 font-medium',
                      )}
                    >
                      {item.title}
                    </span>
                  )}

                  {item.matched_keywords && item.matched_keywords.length > 0 && (
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      {item.matched_keywords.map(kw => (
                        <span key={kw} className="badge-neutral text-[10px]">{kw}</span>
                      ))}
                    </div>
                  )}

                  {item.tickers_mentioned && item.tickers_mentioned.length > 0 && (
                    <div className="flex gap-1.5 mt-1 flex-wrap">
                      {item.tickers_mentioned.map(t => (
                        <TickerLink key={t} ticker={t} className="text-[11px]" />
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function NewsPage() {
  const {
    items,
    breaking,
    secFilings,
    secFilingsLoading,
    secFilingsError,
    loading,
    error,
    lastFetched,
    refetch,
    fetchSecFilings,
  } = useNews()

  const [activeTab, setActiveTab] = useState<PageTab>('tape')

  // Enrich items with breaking urgency tags
  const breakingTitles = new Set(breaking.map(b => b.title.toLowerCase().trim().slice(0, 60)))
  const enrichedItems = items.map(item => {
    const isBreaking = breakingTitles.has(item.title.toLowerCase().trim().slice(0, 60))
    const matchedBreaking = isBreaking
      ? breaking.find(b => b.title.toLowerCase().trim().slice(0, 60) === item.title.toLowerCase().trim().slice(0, 60))
      : null
    return {
      ...item,
      urgency: matchedBreaking?.urgency ?? item.urgency,
      matched_keywords: matchedBreaking?.matched_keywords ?? item.matched_keywords,
    }
  })

  // Fetch SEC filings when that tab is first activated
  const [secFetched, setSecFetched] = useState(false)
  useEffect(() => {
    if (activeTab === 'sec' && !secFetched) {
      fetchSecFilings(7, 'all')
      setSecFetched(true)
    }
  }, [activeTab, secFetched, fetchSecFilings])

  function handleSecFilterChange(formType: string) {
    fetchSecFilings(7, formType)
  }

  return (
    <div className="space-y-5 animate-fade-in">
      <PageHeader
        title="News"
        subtitle={
          activeTab === 'tape'
            ? 'Live tape from collected feeds -- polls every 30s while visible'
            : lastFetched ? `Updated ${format(lastFetched, 'HH:mm:ss')} -- auto-refreshes every 2 min` : undefined
        }
        actions={
          <button type="button" onClick={refetch} className="btn-secondary gap-1.5 text-xs" disabled={loading}>
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
            Refresh
          </button>
        }
      />

      {/* Fetch-failure banner — allSettled used to swallow these silently */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={16} className="text-rose-400 shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-rose-300">{error}</p>
        </div>
      )}

      {/* Scrolling ticker */}
      <NewsTicker items={breaking.length > 0 ? breaking : enrichedItems.slice(0, 20)} />

      {/* Breaking alert bar (always visible if there is breaking news) */}
      {breaking.length > 0 && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={16} className="text-rose-400 shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-rose-400">Breaking News</p>
            <ul className="space-y-1">
              {breaking.slice(0, 5).map((b, i) => (
                <li key={i}>
                  {b.url ? (
                    <a
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-text-primary hover:text-rose-300 transition-colors"
                    >
                      {b.title}
                    </a>
                  ) : (
                    <span className="text-sm text-text-primary">{b.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Main tab bar */}
      <div className="glass rounded-xl p-1 flex items-center gap-1 overflow-x-auto">
        {PAGE_TABS.map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={clsx(
              'px-5 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === tab
                ? 'bg-blue/15 text-blue-light'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
            )}
          >
            {PAGE_TAB_LABELS[tab]}
            {tab === 'breaking' && breaking.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-bold">
                {breaking.length > 9 ? '9+' : breaking.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'tape' && <NewsTape />}
      {activeTab === 'news' && (
        <DataSection
          loading={loading && items.length === 0}
          empty={!loading && items.length === 0}
          emptyMessage="News auto-refreshes every 2 minutes — no articles yet."
          onRetry={refetch}
          skeletonRows={8}
        >
          <AllNewsTab items={enrichedItems} loading={loading} />
        </DataSection>
      )}
      {activeTab === 'sec' && (
        <SecFilingsTab
          filings={secFilings}
          loading={secFilingsLoading}
          error={secFilingsError}
          onFilterChange={handleSecFilterChange}
        />
      )}
      {activeTab === 'breaking' && (
        <BreakingTab breaking={breaking} />
      )}
    </div>
  )
}
